import { useEffect } from "react";
import { LIVE_REFRESH_FALLBACK_MS, LIVE_REFRESH_MIN_REFOCUS_GAP_MS } from "@/features/patient/records/utils/pollingConfig";

/**
 * Safety net for anything that's supposed to update live over the realtime
 * socket (see src/lib/socketClient.ts) — refetches on a periodic interval
 * AND immediately whenever the tab regains focus/visibility, so a real user
 * is never stuck looking at stale data for longer than the interval (or
 * after switching back to this tab) even if the socket connection is down
 * for any reason: a missed reconnect, a corporate network blocking
 * WebSockets, a misconfigured proxy, a temporary backend hiccup, or
 * anything else that isn't predictable in advance.
 *
 * The socket push, when it's working, still makes updates feel instant —
 * this only covers the gap when it isn't. Check the browser console for
 * "[realtime]" logs first if data ever needs this fallback to catch up.
 *
 * The socket is currently disabled entirely (socketClient.ts's syncAuth is
 * a no-op) — so for every caller of this hook, THIS is the only live-update
 * mechanism running, not a rare-case fallback. That matters for the
 * focus/visibilitychange listener below: without a minimum gap between
 * refetches, every native file-picker dialog opening or closing blurs then
 * refocuses the tab, firing an immediate refetch on every such blip — which
 * is what turned this into dozens of requests to unrelated endpoints
 * (notifications, family dependents/caregivers/access-requests) during a
 * single upload. A real "away for a while, came back" still refetches
 * immediately; this only collapses focus events arriving faster than that
 * could plausibly happen.
 */
export function useLiveRefreshFallback(refresh: () => void, intervalMs = LIVE_REFRESH_FALLBACK_MS): void {
  useEffect(() => {
    // Starts at -Infinity, not Date.now(): this hook hasn't refreshed at all
    // yet at mount, so the very first focus/visibilitychange event — however
    // soon after mount it arrives — must never be throttled as "too soon
    // since the last refresh". Only a refresh that actually happened should
    // start the gap.
    let lastRefreshAt = -Infinity;
    const doRefresh = () => {
      lastRefreshAt = Date.now();
      refresh();
    };
    const interval = setInterval(doRefresh, intervalMs);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefreshAt < LIVE_REFRESH_MIN_REFOCUS_GAP_MS) return;
      doRefresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refresh, intervalMs]);
}
